import type { InferType } from 'yup'

import {
  type LotteryId as LotteryMappingId,
  LotteryType,
  lotteryTypeByLotteryId,
} from '@lottocom/lottery-mapping'
import { validatePlay } from '@lottocom/price-calculation'
import { defineStore } from 'pinia'

import {
  LotteryType as BackendLotteryType,
  LotteryId,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { LotteryConfig } from '../utils/lotteryCache'

import {
  lotteryLocalStateSchema,
  playSlipPlaySchema,
  type quickPickSchema,
} from './schemas/playSlip'

export enum LotteryTicketType {
  PrepaidSubscription = 'PrepaidSubscription',
  Single = 'Single',
  Subscription = 'Subscription',
}
export type LotteryLocalState = {
  plays: Partial<PlaySlipPlays>
  prepaidSubscriptionAmount: Partial<PrepaidSubscriptionAmount>
  quickPickSettings: Partial<QuickPickSettingsByLotteryId>
  selectedAddOns: Partial<LotterySelectedAddOns>
  ticketType: Partial<TicketType>
}
export type LotterySelectedAddOn = AddOn
export type LotterySelectedPerPlayAddOn = AddOn
export type LotterySelectedPerPlayAddOns = Record<string, LotterySelectedPerPlayAddOn[]>
export type QuickPickSettings = InferType<typeof quickPickSchema>
export type QuickPickSettingsByLotteryId = Record<LotteryId, QuickPickSettings>

type AddOn = { name: string; value: string }

type LotterySelectedAddOns = Record<LotteryId, LotterySelectedAddOn[]>

type PlaySlipPlays = Record<LotteryId, PlaySlipPlay[]>

type PrepaidSubscriptionAmount = Record<LotteryId, Maybe<PrepaidSubscriptionOption>>

type PrepaidSubscriptionOption = {
  label: string
  value: number
}

type TicketType = Record<LotteryId, LotteryTicketType>

const deprecatedAddOns: Partial<Record<LotteryId, string>> = {
  [LotteryId.MegaMillions]: 'megaplier',
}

const DEFAULT_STATE: LotteryLocalState = {
  plays: {},
  prepaidSubscriptionAmount: {},
  quickPickSettings: {},
  selectedAddOns: {},
  ticketType: {},
}

export const DEFAULT_QUICK_PICK_SETTINGS: QuickPickSettings = {
  betType: null,
  numbers: null,
  quantity: 5,
  wager: null,
}

export const deserialize = (value: string) => {
  let state: Maybe<LotteryLocalState> = null

  try {
    const parsedValue = JSON.parse(value)
    state = lotteryLocalStateSchema.cast(parsedValue)
    const isValid = lotteryLocalStateSchema.isValidSync(state)

    if (!state || !isValid) {
      if (import.meta.client) {
        useDatadog().log(DATADOG_LOG_TYPES.Warn, {
          message: 'Invalid LocalStorage',
          messageContext: {
            localStorage: value,
          },
        })
      }

      return DEFAULT_STATE
    }

    // Cast plays
    if (state.plays) {
      typedKeys(state.plays).forEach((lotteryId) => {
        if (!state) return false

        const allPlaysValid = state.plays[lotteryId]?.every((play, index) => {
          if (!state?.plays[lotteryId]) return false

          state.plays[lotteryId][index] = playSlipPlaySchema.cast(play)
          return playSlipPlaySchema.isValidSync(state.plays[lotteryId][index])
        })

        if (!allPlaysValid) {
          state = DEFAULT_STATE
        }
      })
    }
  } catch (error) {
    useDatadog().log(DATADOG_LOG_TYPES.Warn, {
      message: 'Invalid LocalStorage',
      messageContext: {
        error,
        localStorage: value,
      },
    })

    return DEFAULT_STATE
  }

  // Remove deprecated addOns
  if (state.selectedAddOns) {
    typedKeys(state.selectedAddOns).forEach((lotteryId) => {
      if (!state) return

      const deprecatedAddonName = deprecatedAddOns[lotteryId]
      if (!deprecatedAddonName) return
      const currentAddOns = state.selectedAddOns[lotteryId]
      state.selectedAddOns[lotteryId] = currentAddOns?.filter(
        (addon) => addon.name !== deprecatedAddonName,
      )
    })
  }

  if (state.plays) {
    typedKeys(state.plays).forEach((lotteryId) => {
      if (
        lotteryTypeByLotteryId[lotteryId as unknown as LotteryMappingId] ===
        LotteryType.PICK_LOTTERY
      )
        return

      if (!state?.plays[lotteryId]) return

      const emptyPlays = state.plays[lotteryId].filter(({ isDirty }) => !isDirty)
      const dirtyButNotCompletePlays = state.plays[lotteryId].filter(
        ({ isComplete, isDirty }) => isDirty && !isComplete,
      )
      const hasNotToReorderPlayIds =
        emptyPlays.length === 1 && dirtyButNotCompletePlays.length === 0

      let hasEmptyPlays = false
      state.plays[lotteryId] = state.plays[lotteryId].reduce<PlaySlipPlay[]>(
        (accumulator, play) => {
          const updatedPlay: PlaySlipPlay = {
            ...play,
            id: hasNotToReorderPlayIds ? play.id : `play-${accumulator.length + 1}`,
            key: play.key ?? `play-key-${crypto.randomUUID()}`,
          }
          if (play.isDirty) accumulator.push(updatedPlay)
          if (!play.isDirty && !hasEmptyPlays) {
            accumulator.push(updatedPlay)
            hasEmptyPlays = true
          }
          return accumulator
        },
        [],
      )
    })
  }

  if (state.plays) {
    typedKeys(state.plays).forEach((lotteryId) => {
      if (state?.plays[lotteryId]?.length === 0) {
        // oxlint-disable-next-line typescript/no-dynamic-delete */
        delete state.plays[lotteryId]
      }
    })
  }

  if (import.meta.client) {
    const nuxtApp = useNuxtApp()
    nextTick(() => {
      nuxtApp.runWithContext(() => {
        const route = useRouter().currentRoute.value
        const lotteryId = getLotteryIdInCorrectCase(
          String(route.params.lotteryId || route.query?.p),
        )
        if (!lotteryId) return
        const store = useLotteryStore()
        if (!store.plays[lotteryId]) return
        store.validateStoredPlays(lotteryId)
      })
    })
  }

  return state
}

export const updateNumberLotteryPlay = (
  config: LotteryConfig,
  playIndex: number,
  play: Maybe<Partial<PlaySlipPlay>>,
): PlaySlipPlay => {
  const { fields, isDirty, isFieldComplete, perPlayAddOn } = getPlayInformation(config, play)

  return {
    fields,
    id: `play-${playIndex + 1}`,
    isComplete: isFieldComplete,
    isDirty: !!isDirty,
    isQuickPick: !!play?.isQuickPick,
    isValid: isFieldComplete,
    key: play?.key ?? `play-key-${crypto.randomUUID()}`,
    perPlayAddOn,
    stake: play?.stake ?? getInitialStake(config),
  }
}

export const updatePickLotteryPlay = (
  lotteryId: LotteryId,
  config: LotteryConfig,
  playIndex: number,
  play: Maybe<Partial<PlaySlipPlay>>,
): PlaySlipPlay => {
  const { fields, isDirty, isFieldComplete, perPlayAddOn } = getPlayInformation(config, play)

  const isValid =
    isFieldComplete &&
    !!play?.isValid &&
    validatePlay(
      {
        addOns: perPlayAddOn ? [perPlayAddOn] : [],
        fields,
        stake: {
          price: play.stake || getInitialStake(config),
        },
      },
      lotteryId,
    )

  return {
    fields,
    id: `play-${playIndex + 1}`,
    isComplete: isFieldComplete && isValid,
    isDirty: !!isDirty,
    isQuickPick: !!play?.isQuickPick,
    isValid,
    key: play?.key ?? `play-key-${crypto.randomUUID()}`,
    perPlayAddOn,
    stake: play?.stake ?? getInitialStake(config),
  }
}

const getInitialPlays = (lotteryId: LotteryId, config?: LotteryConfig): PlaySlipPlay[] => {
  if (!config) return []

  return config.type === BackendLotteryType.PickLottery
    ? [
        updatePickLotteryPlay(lotteryId, config, 0, {
          perPlayAddOn: getInitialPerPlayAddOns(config)[0],
        }),
      ]
    : [
        updateNumberLotteryPlay(config, 0, {
          perPlayAddOn: getInitialPerPlayAddOns(config)[0],
        }),
      ]
}

const getPlayInformation = (config: LotteryConfig, play: Maybe<Partial<PlaySlipPlay>>) => {
  const perPlayAddOn = play?.perPlayAddOn || null

  const isIgnoredForCurrentAddOn = (
    ignoredForAddOns: LotteryConfig['numbers'][number]['ignoredForAddOns'],
  ) => ignoredForAddOns.some(({ value }) => value === perPlayAddOn?.value)

  const fields =
    config.numbers.map(({ name }, fieldIndex) => ({
      name,
      selected: play?.fields?.[fieldIndex]?.selected ?? [],
    })) ?? []

  const isDirty = config.numbers.some(({ ignoredForAddOns }, fieldIndex) => {
    const currentField = play?.fields?.[fieldIndex]
    return !!currentField?.selected.length && !isIgnoredForCurrentAddOn(ignoredForAddOns)
  })

  const isFieldComplete = config.numbers.every(
    ({ ignoredForAddOns, selectableMax }, fieldIndex) => {
      if (!play) return false
      const currentField = play.fields?.[fieldIndex]

      return (
        (currentField?.selected && currentField.selected.length >= selectableMax) ||
        isIgnoredForCurrentAddOn(ignoredForAddOns)
      )
    },
  )

  return {
    fields,
    isDirty,
    isFieldComplete,
    perPlayAddOn,
  }
}

export const useLotteryStore = defineStore('ltc_lottery', {
  actions: {
    addPlay(lotteryId: LotteryId, play: PlaySlipPlay) {
      this.plays[lotteryId]?.push(play)
    },
    deleteLotterySelection(lotteryId: LotteryId) {
      /* oxlint-disable typescript/no-dynamic-delete */
      delete this.plays[lotteryId]
      delete this.selectedAddOns[lotteryId]
      delete this.ticketType[lotteryId]
      delete this.prepaidSubscriptionAmount[lotteryId]
      /* oxlint-enable typescript/no-dynamic-delete */
    },
    deletePrepaidSubscriptionAmount(lotteryId: LotteryId) {
      /* oxlint-disable typescript/no-dynamic-delete */
      delete this.prepaidSubscriptionAmount[lotteryId]
      /* oxlint-enable typescript/no-dynamic-delete */
    },
    removeAllIncompletePlays(lotteryId: LotteryId) {
      this.plays[lotteryId] = this.plays[lotteryId]?.filter((play) => play.isComplete)
    },
    removePlay(lotteryId: LotteryId, index: number) {
      this.plays[lotteryId]?.splice(index, 1)
    },
    setPlays(lotteryId: LotteryId, plays: PlaySlipPlay[]) {
      this.plays[lotteryId] = plays
    },
    setPrepaidSubscriptionAmount(lotteryId: LotteryId, amount: Maybe<PrepaidSubscriptionOption>) {
      this.prepaidSubscriptionAmount[lotteryId] = amount
    },
    setQuickPickSettings(lotteryId: LotteryId, settings: QuickPickSettings) {
      this.quickPickSettings[lotteryId] = settings
    },
    setSelectedAddOns(lotteryId: LotteryId, addOns: { name: string; value: string }[]) {
      this.selectedAddOns[lotteryId] = addOns
    },
    setTicketType(lotteryId: LotteryId, ticketType: LotteryTicketType) {
      this.ticketType[lotteryId] = ticketType
    },
    validateStoredPlays(lotteryId: LotteryId) {
      const licensedTerritory = useLicensedTerritory({ raw: true })
      const queryResult = useLotteryQuery({ licensedTerritory, lotteryId })

      let hasValidated = false

      queryResult.onResult(({ data }) => {
        const config = data?.lottery.currentConfig
        if (!config || hasValidated) return
        hasValidated = true

        const storedPlays = this.plays[lotteryId]
        if (!storedPlays?.length) return

        const isWithinRange = (play: PlaySlipPlay) =>
          config.numbers.every(({ max, min }, fieldIndex) =>
            (play.fields[fieldIndex]?.selected ?? []).every((num) => num >= min && num <= max),
          )

        const validPlays = storedPlays.filter(isWithinRange)

        if (validPlays.length === storedPlays.length) return

        const isPickLottery = config.type === BackendLotteryType.PickLottery

        const reindexedPlays = validPlays.map((play, index) =>
          isPickLottery
            ? updatePickLotteryPlay(lotteryId, config, index, play)
            : updateNumberLotteryPlay(config, index, play),
        )

        this.plays[lotteryId] = reindexedPlays.length
          ? reindexedPlays
          : getInitialPlays(lotteryId, config)
      })
    },
    updatePlay(
      lotteryId: LotteryId,
      config: Maybe<LotteryConfig>,
      playId: string,
      play: Partial<PlaySlipPlay>,
    ) {
      if (!config) return

      const isPickLottery = config.type === BackendLotteryType.PickLottery

      if (!this.plays[lotteryId]) {
        this.plays[lotteryId] = getInitialPlays(lotteryId, config)
      }

      const lotteryPlays = this.plays[lotteryId] || []
      const playIndex = lotteryPlays.findIndex((p) => p && p.id === playId)

      if (playIndex === -1) return

      lotteryPlays[playIndex] = {
        ...lotteryPlays[playIndex],
        ...play,
        ...{
          isQuickPick: play.isQuickPick ?? false,
        },
      }

      lotteryPlays[playIndex] = isPickLottery
        ? updatePickLotteryPlay(lotteryId, config, playIndex, lotteryPlays[playIndex])
        : updateNumberLotteryPlay(config, playIndex, lotteryPlays[playIndex])

      this.plays[lotteryId] = lotteryPlays
    },
  },
  getters: {
    getCurrentPlays: (state) => (lotteryId: LotteryId) => state.plays[lotteryId],
    playsByLottery: (state) => (lotteryId: LotteryId, config: LotteryConfig) =>
      state.plays[lotteryId] ?? getInitialPlays(lotteryId, config),
    prepaidSubscriptionAmountByLottery: (state) => (lotteryId: LotteryId) =>
      state.prepaidSubscriptionAmount[lotteryId],
    quickPickSettingsByLottery: (state) => (lotteryId: LotteryId) =>
      state.quickPickSettings[lotteryId] ?? DEFAULT_QUICK_PICK_SETTINGS,
    selectedAddOnsByLottery: (state) => (lotteryId: LotteryId, config: LotteryConfig) =>
      state.selectedAddOns[lotteryId] ?? getInitialSelectedAddOns(config),
    ticketTypeByLottery: (state) => (lotteryId: LotteryId) =>
      state.ticketType[lotteryId] ?? LotteryTicketType.Single,
  },
  persist: {
    serializer: {
      deserialize,
      serialize: (value) => JSON.stringify(value),
    },
    storage: piniaPluginPersistedstate.localStorage(),
  },
  state: (): LotteryLocalState => ({
    plays: {},
    prepaidSubscriptionAmount: {},
    quickPickSettings: {},
    selectedAddOns: {},
    ticketType: {},
  }),
})
